import { TrendingDown, TrendingUp, Minus } from 'lucide-react';

/**
 * Shared news-badge primitives used across StockModal NewsTab,
 * PortfolioView inline news, and WatchlistNews. Centralizes the
 * provider+sentiment chip styling so all three surfaces stay in
 * visual lockstep — adding a new provider only needs touching one
 * file.
 */

const PROVIDER_LABEL = { yahoo: 'Yahoo', finnhub: 'Finnhub', massive: 'Massive', freenews: 'FreeNews' };

// Hand-written gradient class strings for every provider × sentiment
// combo so Tailwind's JIT picks them all up at compile time. Provider
// color sits on the left, sentiment color flows in on the right.
const COMBO_CLASS = {
  'yahoo|Bullish':      'bg-gradient-to-r from-yellow-100 to-emerald-100 text-emerald-900 dark:from-yellow-900/40 dark:to-emerald-900/40 dark:text-emerald-200 ring-yellow-300/40 dark:ring-yellow-700/40',
  'yahoo|Bearish':      'bg-gradient-to-r from-yellow-100 to-rose-100 text-rose-900 dark:from-yellow-900/40 dark:to-rose-900/40 dark:text-rose-200 ring-yellow-300/40 dark:ring-yellow-700/40',
  'yahoo|Neutral':      'bg-gradient-to-r from-yellow-100 to-slate-100 text-yellow-900 dark:from-yellow-900/40 dark:to-slate-800/60 dark:text-yellow-200 ring-yellow-300/40 dark:ring-yellow-700/40',
  'finnhub|Bullish':    'bg-gradient-to-r from-sky-100 to-emerald-100 text-emerald-900 dark:from-sky-900/40 dark:to-emerald-900/40 dark:text-emerald-200 ring-sky-300/40 dark:ring-sky-700/40',
  'finnhub|Bearish':    'bg-gradient-to-r from-sky-100 to-rose-100 text-rose-900 dark:from-sky-900/40 dark:to-rose-900/40 dark:text-rose-200 ring-sky-300/40 dark:ring-sky-700/40',
  'finnhub|Neutral':    'bg-gradient-to-r from-sky-100 to-slate-100 text-sky-900 dark:from-sky-900/40 dark:to-slate-800/60 dark:text-sky-200 ring-sky-300/40 dark:ring-sky-700/40',
  // Phase E (2026-05-16): Massive — violet/indigo gradient so cards
  // are visually distinct from Yahoo (yellow) at a glance.
  'massive|Bullish':    'bg-gradient-to-r from-violet-100 to-emerald-100 text-emerald-900 dark:from-violet-900/40 dark:to-emerald-900/40 dark:text-emerald-200 ring-violet-300/40 dark:ring-violet-700/40',
  'massive|Bearish':    'bg-gradient-to-r from-violet-100 to-rose-100 text-rose-900 dark:from-violet-900/40 dark:to-rose-900/40 dark:text-rose-200 ring-violet-300/40 dark:ring-violet-700/40',
  'massive|Neutral':    'bg-gradient-to-r from-violet-100 to-slate-100 text-violet-900 dark:from-violet-900/40 dark:to-slate-800/60 dark:text-violet-200 ring-violet-300/40 dark:ring-violet-700/40',
  // Oct 2026: FreeNewsApi.io (replaced Yahoo) — amber/orange gradient.
  'freenews|Bullish':   'bg-gradient-to-r from-amber-100 to-emerald-100 text-emerald-900 dark:from-amber-900/40 dark:to-emerald-900/40 dark:text-emerald-200 ring-amber-300/40 dark:ring-amber-700/40',
  'freenews|Bearish':   'bg-gradient-to-r from-amber-100 to-rose-100 text-rose-900 dark:from-amber-900/40 dark:to-rose-900/40 dark:text-rose-200 ring-amber-300/40 dark:ring-amber-700/40',
  'freenews|Neutral':   'bg-gradient-to-r from-amber-100 to-slate-100 text-amber-900 dark:from-amber-900/40 dark:to-slate-800/60 dark:text-amber-200 ring-amber-300/40 dark:ring-amber-700/40',
};

/**
 * ProviderSentimentChip — single gradient pill combining article
 * provenance with title-derived sentiment verdict.
 *
 * `compact` (default false): smaller variant for inline news strips
 * (PortfolioView / WatchlistNews) — text-[9px] vs text-[10px] and
 * tighter padding.
 */
export function ProviderSentimentChip({ provider, sentiment, compact = false }) {
  const label = PROVIDER_LABEL[provider] || (provider ? provider.charAt(0).toUpperCase() + provider.slice(1) : 'Source');
  const sent = sentiment || 'Neutral';
  const cls = COMBO_CLASS[`${provider}|${sent}`] || 'bg-muted text-muted-foreground ring-border/40';
  const Icon = sent === 'Bullish' ? TrendingUp : sent === 'Bearish' ? TrendingDown : Minus;
  const sizing = compact
    ? 'px-1.5 py-0 text-[9px] gap-0.5'
    : 'px-1.5 py-0.5 text-[10px] gap-1';
  const iconSize = compact ? 'w-2.5 h-2.5' : 'w-3 h-3';
  return (
    <span
      className={`inline-flex items-center rounded font-semibold ring-1 ${sizing} ${cls}`}
      data-testid={`news-provider-sentiment-${provider}-${sent}`}
      title={`Source: ${label} · Sentiment: ${sent}`}
    >
      <Icon className={iconSize} />
      {label} · {sent}
    </span>
  );
}

/**
 * NewsSourceMix — top-of-list summary chip showing how many articles
 * came from each provider (e.g. "3 Yahoo · 2 Finnhub").
 */
export function NewsSourceMix({ articles }) {
  if (!Array.isArray(articles) || articles.length === 0) return null;
  const counts = articles.reduce((acc, a) => {
    const p = a.provider || 'other';
    acc[p] = (acc[p] || 0) + 1;
    return acc;
  }, {});
  const order = ['massive', 'freenews', 'yahoo', 'finnhub', 'other'];
  const labelMap = { massive: 'Massive', freenews: 'FreeNews', yahoo: 'Yahoo', finnhub: 'Finnhub', other: 'Other' };
  const colorMap = {
    massive:    'text-violet-700 dark:text-violet-300',
    yahoo:      'text-yellow-700 dark:text-yellow-300',
    finnhub:    'text-sky-700 dark:text-sky-300',
    other:      'text-muted-foreground',
  };
  const present = order.filter((p) => counts[p]);
  return (
    <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground" data-testid="news-source-mix">
      <span className="uppercase tracking-wider font-semibold">Sources</span>
      {present.map((p, i) => (
        <span key={p} className="flex items-center gap-1.5">
          {i > 0 && <span className="text-muted-foreground/40">·</span>}
          <span className={`font-semibold ${colorMap[p]}`}>
            {counts[p]} {labelMap[p]}
          </span>
        </span>
      ))}
    </div>
  );
}

/**
 * NewsSentimentSummary — at-a-glance "vibe" pill showing the
 * Bullish/Bearish/Neutral percentage breakdown across all loaded
 * articles. Renders nothing on small samples (<2 articles) or when
 * sentiments aren't classified at all.
 */
export function NewsSentimentSummary({ articles }) {
  if (!Array.isArray(articles) || articles.length < 2) return null;
  const total = articles.length;
  const counts = { Bullish: 0, Bearish: 0, Neutral: 0 };
  articles.forEach((a) => {
    const s = a.sentiment || 'Neutral';
    if (counts[s] != null) counts[s] += 1;
    else counts.Neutral += 1;
  });
  if (counts.Bullish === 0 && counts.Bearish === 0 && counts.Neutral === total) return null;
  // Determine the dominant sentiment for the leading pill color.
  const dominant = ['Bullish', 'Bearish', 'Neutral']
    .reduce((best, k) => counts[k] > counts[best] ? k : best, 'Neutral');
  const dominantClass = {
    Bullish: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 ring-emerald-500/30',
    Bearish: 'bg-rose-500/15 text-rose-700 dark:text-rose-300 ring-rose-500/30',
    Neutral: 'bg-muted text-muted-foreground ring-border/40',
  }[dominant];
  const DominantIcon = dominant === 'Bullish' ? TrendingUp : dominant === 'Bearish' ? TrendingDown : Minus;
  const pct = (n) => Math.round((n / total) * 100);
  // Render only the non-zero categories in dominant-first order.
  const order = ['Bullish', 'Bearish', 'Neutral'].sort((a, b) => counts[b] - counts[a]);
  const segments = order
    .filter((k) => counts[k] > 0)
    .map((k) => `${pct(counts[k])}% ${k}`);
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ring-1 ${dominantClass}`}
      data-testid="news-sentiment-summary"
      title={`Sentiment mix across ${total} articles: ${order.map(k => `${counts[k]} ${k}`).join(' · ')}`}
    >
      <DominantIcon className="w-3 h-3" />
      <span className="uppercase tracking-wider">Vibe</span>
      <span className="font-mono">·</span>
      <span>{segments.join(' · ')}</span>
    </span>
  );
}


/**
 * MassiveInsightReasoning — small inline "Why bullish/bearish/neutral"
 * snippet sourced from Massive's per-ticker `insights[*].sentiment_
 * reasoning` field. Only renders when the article is from Massive AND
 * carries editorial reasoning (cross-listed mentions on a Massive
 * article won't have ticker-specific insight, so we render nothing).
 *
 * Designed to be visible (not hover-only) so users immediately see
 * the unique editorial value Massive provides — a one-sentence
 * "Why this matters for THIS ticker" line you can't get from Yahoo
 * or Finnhub.
 *
 * Props:
 *   article: { provider, sentiment, insight_reasoning }
 *   compact: true → smaller padding/text for inline strips
 *            (PortfolioView, WatchlistNews); default false for full
 *            article cards in the Stock Modal NewsTab.
 */
export function MassiveInsightReasoning({ article, compact = false }) {
  if (!article || article.provider !== 'massive') return null;
  const reasoning = article.insight_reasoning;
  if (!reasoning || typeof reasoning !== 'string' || reasoning.trim().length < 4) return null;
  const sent = article.sentiment || 'Neutral';
  // Subtle accent matching the sentiment so a bull-case reads green-ish,
  // bear-case rose-ish, neutral stays muted violet.
  const accent = {
    Bullish: 'border-emerald-500/40 bg-emerald-50/60 dark:bg-emerald-950/30 text-emerald-900 dark:text-emerald-200',
    Bearish: 'border-rose-500/40 bg-rose-50/60 dark:bg-rose-950/30 text-rose-900 dark:text-rose-200',
    Neutral: 'border-violet-500/40 bg-violet-50/60 dark:bg-violet-950/30 text-violet-900 dark:text-violet-200',
  }[sent] || 'border-violet-500/40 bg-violet-50/60';
  const labelText = sent === 'Bullish' ? 'Why bullish' : sent === 'Bearish' ? 'Why bearish' : 'Why this matters';
  const padding = compact ? 'px-1.5 py-0.5' : 'px-2 py-1';
  const textSize = compact ? 'text-[10px]' : 'text-[11px]';
  const labelSize = compact ? 'text-[8px]' : 'text-[9px]';
  return (
    <div
      className={`mt-1 rounded border ${accent} ${padding}`}
      data-testid={`massive-insight-${sent.toLowerCase()}`}
      title={`Editorial reasoning from Massive's per-ticker insights — ${sent} take`}
    >
      <span className={`uppercase tracking-wider font-bold opacity-70 ${labelSize}`}>{labelText} · </span>
      <span className={`italic ${textSize}`}>{reasoning}</span>
    </div>
  );
}



/**
 * KeywordChipRow — clickable Massive-keyword chips that filter the
 * containing news list. Only renders when the article carries
 * Massive-supplied `keywords[]`. `selected` is the currently-active
 * filter keyword (or null); chips are highlighted when they match.
 */
export function KeywordChipRow({ article, selected, onToggle }) {
  if (!article || article.provider !== 'massive') return null;
  const kws = Array.isArray(article.keywords) ? article.keywords.filter(Boolean) : [];
  if (kws.length === 0) return null;
  const shown = kws.slice(0, 5);
  return (
    <div className="flex flex-wrap items-center gap-1 mt-1" data-testid="news-keyword-chip-row">
      {shown.map((kw) => {
        const norm = kw.toLowerCase();
        const isActive = selected && selected.toLowerCase() === norm;
        return (
          <button
            key={kw}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggle(isActive ? null : kw);
            }}
            className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider transition-colors ring-1 ${
              isActive
                ? 'bg-violet-600 text-white ring-violet-700 hover:bg-violet-700'
                : 'bg-violet-100/60 text-violet-800 ring-violet-300/50 hover:bg-violet-200 dark:bg-violet-900/30 dark:text-violet-200 dark:ring-violet-700/40 dark:hover:bg-violet-900/60'
            }`}
            data-testid={`news-keyword-chip-${norm}`}
            title={isActive ? `Click to clear the "${kw}" filter` : `Click to show only articles tagged "${kw}"`}
          >
            #{kw}
          </button>
        );
      })}
    </div>
  );
}

/**
 * ActiveKeywordFilter — banner at the top of the news list when a
 * keyword chip is active. Tells the user what's filtered and offers
 * a one-click clear.
 */
export function ActiveKeywordFilter({ keyword, matchCount, totalCount, onClear }) {
  if (!keyword) return null;
  return (
    <div
      className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md bg-violet-100/70 dark:bg-violet-950/40 ring-1 ring-violet-400/50 dark:ring-violet-700/50"
      data-testid="news-active-keyword-filter"
    >
      <div className="text-[11px] text-violet-900 dark:text-violet-200">
        <span className="opacity-70 uppercase tracking-wider font-semibold">Filtered by</span>
        <span className="ml-1.5 font-bold">#{keyword}</span>
        <span className="ml-2 opacity-70">— showing {matchCount} of {totalCount}</span>
      </div>
      <button
        type="button"
        onClick={onClear}
        className="text-[10px] font-bold text-violet-700 dark:text-violet-300 hover:text-violet-900 dark:hover:text-violet-100 underline-offset-2 hover:underline"
        data-testid="news-clear-keyword-filter"
      >
        Clear ×
      </button>
    </div>
  );
}
